import classNames from "classnames"
import React from "react"

import { Color } from "../../../../constants/V2/color"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Typography from "../../../elements/V2/Typography"
import LayoutItem from "../../Layouts/LayoutItem"

import Badge from "@components/elements/V2/Badge"
import YocoLogoIcon from "@components/elements/V2/Icons/YocoLogoIcon"
import Video from "@components/elements/V2/Video"
import { useScrollAnimateSelf } from "@hooks/V2/useScrollAnimate"
import {
  getAccentColor,
  getBackgroundColorClass,
  getFillColorClass,
  getGradientFromColorClass,
  getGradientToColorClass,
} from "@utils/V2/color"

type Props = {
  cardBackgroundColor: Color
  secondaryBackgroundColor?: Color
  header: string
  superscript?: string
  description?: string
  verticalLabel?: string
  badgeText?: string
  badgeColor: Color
  reverseOrder?: boolean
  size?: "small" | "large"
  buttonText: string
  buttonLink: string
  buttonBorderColor: Color
  buttonStyle?: "solid" | "bordered"
  imageUrl?: string
  imageAlt?: string
  showLogo?: boolean
  headerLabel?: string
  mobileGridOrder?: number
  disclaimer?: string
  descriptionHeader?: string
  videoUrl?: string
} & Partial<Storyblok.CTATrackingEvent>

const ContentBanner = ({
  cardBackgroundColor,
  secondaryBackgroundColor,
  header,
  superscript,
  description,
  verticalLabel,
  badgeText,
  badgeColor,
  reverseOrder = false,
  size = "small",
  buttonText,
  buttonLink,
  buttonBorderColor,
  buttonStyle = "bordered",
  imageUrl,
  imageAlt,
  showLogo = false,
  headerLabel,
  mobileGridOrder,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  disclaimer,
  descriptionHeader,
  videoUrl,
  ...props
}: Props) => {
  const { animationRefCallback } = useScrollAnimateSelf(true)

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      ref={animationRefCallback}
      className={classNames(
        "rounded-lg col-span-4 grid grid-cols-1 overflow-hidden sm:col-span-4 sm:grid-cols-2 md:col-span-6 lg:col-span-12",
        !secondaryBackgroundColor
          ? getBackgroundColorClass(cardBackgroundColor)
          : [
              "bg-gradient-to-br",
              getGradientFromColorClass(cardBackgroundColor),
              getGradientToColorClass(secondaryBackgroundColor),
            ]
      )}
      {...props}
    >
      <div
        className={classNames(
          "flex flex-col px-20 py-24 sm:py-32 md:px-32 md:py-48",
          (showLogo || headerLabel) && size === "large"
            ? "sm:gap-32"
            : "sm:gap-16",
          size === "large" ? "justify-center gap-32" : "gap-20 md:gap-32"
        )}
      >
        {badgeText && (
          <Badge
            text={badgeText}
            textColor={getAccentColor(badgeColor)}
            accentColor={badgeColor}
            uppercaseText={true}
            style="solid"
          />
        )}
        {(showLogo || headerLabel) && (
          <div className="flex items-center justify-start gap-12">
            {showLogo && (
              <YocoLogoIcon
                height="32"
                className={classNames(
                  "w-auto items-start",
                  getFillColorClass(Color.Blue)
                )}
              />
            )}
            {headerLabel && (
              <Typography
                text={headerLabel}
                size="body-lg"
                font="grotesk"
                weight="medium"
                color={getAccentColor(cardBackgroundColor)}
                className="mt-[2px]"
              />
            )}
          </div>
        )}
        <div className="flex flex-col gap-4">
          {superscript && (
            <Typography
              text={superscript}
              font="grotesk"
              weight="book"
              size="subscript-lg"
              color={getAccentColor(cardBackgroundColor)}
              className="uppercase"
            />
          )}
          <Typography
            text={header}
            font="grotesk"
            weight={size === "large" ? "medium" : "book"}
            size={size === "large" ? "h5" : "lead-lg"}
            color={getAccentColor(cardBackgroundColor)}
          />
          {disclaimer && (
            <Typography
              text={disclaimer}
              font="grotesk"
              weight="book"
              size="body-sm"
              color={getAccentColor(cardBackgroundColor)}
            />
          )}
          {descriptionHeader && (
            <Typography
              text={descriptionHeader}
              font="grotesk"
              weight="medium"
              size="lead-md"
              color={getAccentColor(cardBackgroundColor)}
              className="mt-[20px]"
            />
          )}
          {description && (
            <Typography
              text={description}
              font="grotesk"
              weight="book"
              size="body-md"
              color={getAccentColor(cardBackgroundColor)}
            />
          )}
        </div>
        <PillButton
          size="large"
          style={buttonStyle}
          text={buttonText}
          color={buttonBorderColor}
          linkUrl={buttonLink}
          overrideTextColor={
            buttonStyle === "solid"
              ? getAccentColor(buttonBorderColor)
              : getAccentColor(cardBackgroundColor)
          }
          trackingEvent={trackingEvent}
          trackingEventKey={trackingEventKey}
          trackingEventValue={trackingEventValue}
        />
      </div>
      <div
        className={classNames("relative flex sm:justify-center", {
          "row-start-1 sm:col-start-2": reverseOrder,
        })}
      >
        <div
          className={classNames("flex w-full items-center justify-center", {
            "aspect-square w-full md:absolute md:left-0 md:top-1/2 md:-translate-y-1/2":
              size === "small",
          })}
        >
          {videoUrl && (
            <Video
              video={videoUrl}
              playbackMode="autoplay"
              aspectRatio="1:1"
              coverImage={imageUrl ?? ""}
            />
          )}
          {!videoUrl && imageUrl && (
            <GatsbyStoryblokImage
              image={imageUrl}
              alt={imageAlt || "Banner image"}
              className={classNames(
                size === "large"
                  ? "max-h-[250px] !max-w-[250px] sm:max-h-[430px] sm:!max-w-[430px]"
                  : "h-full w-full",
                {
                  "my-16 sm:m-0": reverseOrder && size === "large",
                },
                { "mr-56": verticalLabel && size === "large" }
              )}
            />
          )}
          {verticalLabel && (
            <div className="absolute right-32 flex -translate-y-16 items-center sm:right-8 sm:-translate-y-1/2 md:right-16 lg:right-[30px]">
              <Typography
                font="grotesk"
                weight="book"
                size="subscript-lg"
                color={getAccentColor(cardBackgroundColor)}
                text={verticalLabel}
                className="origin-center rotate-180 uppercase opacity-50 [writing-mode:vertical-lr]"
              />
            </div>
          )}
        </div>
      </div>
    </LayoutItem>
  )
}

export default ContentBanner
